import Icon from "@/icons";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { User } from "types";

interface Link {
    name: string;
    url: string;
}

interface MessageInviteDisplayProps {
    links: Link[];
    createdBy: User;
}

const fetchLinkDetails = async (url: string) => {
    const res = await fetch(`__SITE_BASE__/api/invites/${url.replace(window.location.origin + "/invite/", '')}?user=true`);
    const data = await res.json();
    return data;
};

export default function MessageInviteDisplay({ links, createdBy }: MessageInviteDisplayProps) {

    const navigate = useNavigate();

    const query = useQuery({
        queryKey: ['linksDetails', links],
        queryFn: async () => {
            const linksDetails = await Promise.all(links.map(async (link) => {
                const data = await fetchLinkDetails(link.url);
                return {
                    ...link,
                    data: data.invite ? data.invite : data
                };
            }));
            return linksDetails;
        },
        enabled: !!links,
        staleTime: 5 * 60 * 1000,
    });

    if (query.isLoading) return <div></div>;
    if (query.error) return <div></div>;

    const joinTeamDirectly = async (team: any, invite: any) => {
        if (team.applicationEnabled) return window.open(`/invite/${invite}`, '_blank');
        await fetch(`__SITE_BASE__/api/v1/invites/${invite}/join`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
        });
        query.refetch();
        return window.location.href = `/teams/${team.team.id}`;
    }

    return (
        <>
            {query.data && query.data.map((link: any, index: number) => (
                <>

                    {!link.data.team && (
                        <div key={index} className="w-auto md:min-w-[30rem] p-4 bg-brand-default rounded-lg shadow-lg border border-opacity-50 border-gray-600 hover:bg-brand-default/25 hover:border-gray-700">
                            
                            <div className="flex justify-between items-center mb-3">
                                <div className="w-10 h-10 bg-brand-darkest rounded-full flex items-center justify-center">
                                    {createdBy!.profilePicture ? (
                                        <img src={createdBy!.profilePicture} className="w-10 h-10 rounded-full" />
                                    ) : (
                                        <span className="text-white text-xl font-bold">{createdBy!.username[0].toUpperCase()}</span>
                                    )}
                                </div>
                                <div className="ml-3">
                                    <h2 className="text-white font-bold">The invite link is invalid</h2>
                                </div>

                                <div className="w-12 h-12 rounded ml-auto bg-brand-darkest items-center justify-center flex ">
                                    <div className="text-gray-500 text-xl">
                                        <Icon name="poop" size="36" />
                                    </div>
                                </div>
                            </div>
                            <div className="items-center justify-center flex">
                                <div className="w-full mt-4 text-center p-2 font-bold opacity-50 cursor-not-allowed bg-red-500 text-white rounded">
                                    Invalid Invite
                                </div>
                            </div>
                        </div>
                    ) || (
                        <>
                            <div key={index} className="w-auto md:min-w-[30rem] bg-brand-default rounded-lg shadow-lg border border-opacity-50 border-gray-600 hover:bg-brand-default/25 hover:border-gray-700">
                            {link?.data?.team?.banner && (
                                <div className="bg-brand-darkest bg-cover bg-center bg-no-repeat rounded-t-lg h-24" style={{ backgroundImage: `url(${link.data.team.banner})` }} />
                            )}
                                <div className="flex justify-between items-center mt-3 px-4 py-1">
                                    <div className="w-10 h-10 bg-brand-darkest rounded-full flex items-center justify-center">
                                        {createdBy!.profilePicture ? (
                                            <img src={createdBy!.profilePicture} className="w-10 h-10 rounded-full" />
                                        ) : (
                                            <span className="text-white text-xl font-bold">{createdBy!.username[0].toUpperCase()}</span>
                                        )}
                                    </div>
                                    <div className="ml-3">
                                        <h2 className="text-white font-bold">Accept {createdBy.username}'s invite to {link.data.team.name}</h2>
                                        <p className="text-gray-400 text-sm">{link.data.team.membersCount} members</p>
                                    </div>

                                    <div className="w-12 h-12 rounded ml-auto bg-brand-darkest items-center justify-center flex ">
                                        {link.data.team.profilePicture ? (
                                            <img src={link.data.team.profilePicture} className="w-12 h-12 rounded" draggable={false} loading="lazy" />
                                        ) : (
                                            <span className="text-white text-xl font-bold">{link.data.team.name[0].toUpperCase()}</span>
                                        )}
                                    </div>

                                </div>

                                <div className="items-center justify-center flex px-4 py-4">
                                    {!link.data.message ? (
                                        <button className="w-full mt-4 p-2 text-center font-bold bg-brand-green hover:bg-brand-green/50 text-white rounded transition duration-200" onClick={() => joinTeamDirectly(link.data, link.url.replace(window.location.origin + "/invite/", ''))}>
                                            {link.data.applicationEnabled ? "Fill Application" : "Join Team"}
                                        </button>
                                    ) : (
                                        <div className="w-full mt-4 text-center p-2 font-bold hover:opacity-75 cursor-pointer bg-gray-500 text-white rounded" onClick={()=>{ navigate(`/teams/${link.data.team.id}`) }}>
                                            Already Joined To Team
                                        </div>
                                    )}
                                </div>
                            </div>
                            </>
                        )}
                </>
            ))}
        </>
    );
}